import { useEffect } from 'react';

import { datadogRumService } from '@lib/datadogRum';

type MissingPropsRecoveryProps = {
  /** Datadog RUM `context` tag, one per page so issues group separately. */
  context: string;
  /** sessionStorage key for the one-reload-per-URL loop guard. */
  reloadMarkerKey: string;
  /** Where to send the visitor if the reloaded visit is still without props. */
  fallbackPath: string;
  errorMessage: string;
};

/**
 * Rendered when an SSG page arrives without its static props. A client-side
 * navigation can do that when the _next/data fetch fails or returns `{}`:
 * a deploy invalidating the open tab's data URLs (FFH-2478) or a
 * next.config redirect intercepting the data request (the Crellin calendar
 * bridge, FFH-2630) are the known vectors. A full load of the same URL is
 * served complete by the current build, so the recovery is one hard reload;
 * if the props are still missing on the reloaded visit, or the loop guard
 * cannot be stored, it falls back to `fallbackPath` rather than a blank page.
 *
 * Recovering before the render throws also takes the page out of the error
 * boundary, which was the only thing reporting this to RUM (FFH-1558), so
 * the effect reports it here instead. `failureMode` separates a recovery
 * that reloaded from one that gave up: skew and redirect interception are
 * cured by the reload, anything still missing its props on a fresh load is a
 * different root cause.
 */
export const MissingPropsRecovery = ({
  context,
  reloadMarkerKey,
  fallbackPath,
  errorMessage
}: MissingPropsRecoveryProps) => {
  useEffect(() => {
    const currentUrl = window.location.href;
    let didAlreadyReload = false;
    try {
      didAlreadyReload = sessionStorage.getItem(reloadMarkerKey) === currentUrl;
      if (!didAlreadyReload) {
        sessionStorage.setItem(reloadMarkerKey, currentUrl);
      }
    } catch {
      didAlreadyReload = true;
    }

    datadogRumService.addError(new Error(errorMessage), {
      context,
      failureMode: didAlreadyReload ? 'redirected-to-landing' : 'reloading-once'
    });

    if (didAlreadyReload) {
      // replace rather than assign: the broken URL should not survive in
      // history for Back to bounce off.
      window.location.replace(fallbackPath);
    } else {
      window.location.reload();
    }
  }, [context, errorMessage, fallbackPath, reloadMarkerKey]);

  return null;
};
